import React, { useState, useEffect, useMemo } from 'react'

export default function Home() {
  return (
    <section id='home' className='relative h-[80vh] w-full overflow-hidden'>
      <HeroCarousel />
    </section>
  )
}

function HeroCarousel() {
  const slides = useMemo(() => [
    {
      key: 'consultation',
      src: '/assets/heroImages/appdevelopment.jpg',
      title: 'Consultation & Services',
      description: 'We deliver technology, operational solutions and training across multiple industries. Our Consultation & Services division focuses on website design & development, cloud computing, data analytics, and app development.',
      hasButtons: true,
      status: 'active'
    },
    {
      key: 'transport',
      src: '/assets/heroImages/mainTransport.jpg',
      title: 'Transport',
      description: 'Revolutionary transport solutions coming to transform logistics and mobility.',
      hasButtons: false,
      status: 'coming-soon'
    },
    {
      key: 'agriculture',
      src: '/assets/heroImages/mainAgriculture.jpg',
      title: 'Agriculture',
      description: 'Advanced agricultural technologies to boost productivity and sustainability.',
      hasButtons: false,
      status: 'coming-soon'
    },
    {
      key: 'energy',
      src: '/assets/heroImages/mainenergy.jpg',
      title: 'Energy',
      description: 'Clean energy solutions for a sustainable future.',
      hasButtons: false,
      status: 'coming-soon'
    },
    {
      key: 'academy',
      src: '/assets/heroImages/mainEducation.jpg',
      title: 'Academy',
      description: 'Professional training and certification programs for digital transformation.',
      hasButtons: false,
      status: 'coming-soon'
    }
  ], [])

  const [index, setIndex] = useState(0)

  useEffect(() => {
    // Preload images for better performance
    const imagePromises = slides.map(slide => {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => resolve()
        img.onerror = () => resolve() // Still resolve on error to not block
        img.src = slide.src
      })
    })

    // Preload critical images immediately
    Promise.all(imagePromises).then(() => {
      // All images loaded, can start carousel
      console.log('Hero images preloaded successfully')
    })

    const t = setInterval(() => {
      setIndex(i => (i + 1) % slides.length)
    }, 6000)
    return () => clearInterval(t)
  }, [slides])

  const prev = () => setIndex(i => (i - 1 + slides.length) % slides.length)
  const next = () => setIndex(i => (i + 1) % slides.length)

  const currentSlide = slides[index]

  return (
    <div className='relative h-full w-full'>
      {/* Background Image */}
      <div
        className='absolute inset-0 bg-center bg-cover transition-all duration-1000 ease-in-out'
        style={{ backgroundImage: `url(${currentSlide.src})` }}
      >
        {/* Dark overlay for better text readability */}
        <div className='absolute inset-0 bg-black/40'></div>
      </div>

      {/* Content Overlay */}
      <div className='relative z-10 h-full flex items-center pl-12 sm:pl-16 md:pl-20 lg:pl-24 pr-4 sm:pr-6 md:pr-8 lg:pr-12'>
        <div className='w-full sm:w-9/12'>
            
            {/* Combined Brand & Division Title */}
            <div className='mb-6 sm:mb-8'>
              <h1 className='text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-comfortaa font-bold leading-tight text-white'>
                Arieri's <span className='text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300'>World {currentSlide.title}</span>
              </h1>
            </div>

            {/* Coming Soon Badge or Description */}
            {currentSlide.status === 'coming-soon' ? (
              <div className='mb-6 sm:mb-8'>
                <span className='inline-block bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-full text-sm sm:text-lg font-semibold mb-4 sm:mb-6'>
                  Coming Soon
                </span>
                <p className='text-lg sm:text-xl md:text-2xl text-gray-100 leading-relaxed'>
                  {currentSlide.description}
                </p>
              </div>
            ) : (
              <div className='mb-6 sm:mb-8'>
                <p className='text-lg sm:text-xl md:text-2xl text-gray-100 leading-relaxed mb-6 sm:mb-8'>
                  {currentSlide.description}
                </p>
                
                {/* Action Buttons for Consultation & Services */}
                {currentSlide.hasButtons && (
                  <div className='flex flex-col sm:flex-row gap-3 sm:gap-4'>
                    <a
                      href='/request-quote'
                      className='group inline-flex items-center justify-center bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-full text-base sm:text-lg font-semibold shadow-lg transition-all duration-300 transform hover:scale-105 text-center'
                    >
                      <span className='flex items-center gap-2'>
                        Get Free Quote
                        <svg className='w-5 h-5 group-hover:translate-x-1 transition-transform duration-300' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 8l4 4m0 0l-4 4m4-4H3' />
                        </svg>
                      </span>
                    </a>
                    <a
                      href='/services'
                      className='group inline-flex items-center justify-center border-2 border-white/80 text-white hover:bg-white hover:text-green-800 px-6 py-3 sm:px-8 sm:py-4 rounded-full text-base sm:text-lg font-semibold transition-all duration-300 text-center backdrop-blur-sm'
                    >
                      <span className='flex items-center gap-2'>
                        <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 14l-7 7m0 0l-7-7m7 7V3' />
                        </svg>
                         learn more
                      </span>
                    </a>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className='absolute inset-0 z-20 pointer-events-none'>
        {/* Previous/Next Buttons */}
        <button
          onClick={prev}
          className='absolute left-2 sm:left-4 md:left-6 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 sm:p-3 text-white text-xl sm:text-2xl transition-all duration-300 pointer-events-auto'
        >
          ‹
        </button>
        <button
          onClick={next}
          className='absolute right-2 sm:right-4 md:right-6 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 sm:p-3 text-white text-xl sm:text-2xl transition-all duration-300 pointer-events-auto'
        >
          ›
        </button>

        {/* Slide Indicators */}
        <div className='absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 sm:gap-3 pointer-events-auto'>
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                i === index ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className='absolute bottom-4 sm:bottom-6 md:bottom-8 right-4 sm:right-6 md:right-8 z-20 text-white animate-bounce hidden sm:block'>
        <div className='flex flex-col items-center gap-2'>
          <span className='text-xs sm:text-sm opacity-75'>Scroll</span>
          <svg className='w-4 h-4 sm:w-6 sm:h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 14l-7 7m0 0l-7-7m7 7V3' />
          </svg>
        </div>
      </div>
    </div>
  )
}
